import { useAuthStore } from '../../stores/auth'

export const authGuard = async (to, from, next) => {
    const authStore = useAuthStore();

    // Special handling for root route
    if (to.path === '/') {
        console.log('Root route navigation guard: Checking auth state');

        // Check if we have tokens in localStorage but the store isn't initialized
        const hasTokensInStorage = !!localStorage.getItem('token') && !!localStorage.getItem('refreshToken');

        // Initialize auth store if not already initialized
        if (!authStore.initialized || (hasTokensInStorage && !authStore.user)) {
            console.log('Root route: Auth store not initialized, initializing...');
            try {
                const initResult = await authStore.initialize();

                if (!initResult) {
                    console.log('Root route: Auth initialization failed, redirecting to login');
                    next('/auth/login');
                    return;
                }
            } catch (error) {
                console.error('Root route: Auth initialization failed:', error);
                next('/auth/login');
                return;
            }
        }

        if (!authStore.isAuthenticated) {
            console.log('Root route: Not authenticated, redirecting to login');
            next('/auth/login');
            return;
        }

        if (!authStore.user || !authStore.user.permissions) {
            console.log('Root route: No user permissions, redirecting to forbidden');
            next('/forbidden');
            return;
        }

        console.log('Root route: User permissions:', authStore.user.permissions);

        // Check user role
        const userRole = authStore.user?.role?.toLowerCase();

        // Define routes in priority order with their required permissions
        let accessibleRoutes;

        if (userRole === 'super_admin') {
            accessibleRoutes = [
                {
                    path: '/dashboard',
                    permissions: ['dashboard.view_quick_actions', 'dashboard.view_alerts', 'dashboard.view_stats']
                },
                { path: '/orders/board', permission: 'orders.view' },
                { path: '/orders', permission: 'orders.view' },
                { path: '/requisitions', permission: 'requisitions.view' },
                { path: '/payments', permission: 'payments.view' },
                { path: '/products', permission: 'products.view' },
                { path: '/clients', permission: 'clients.view' },
                { path: '/suppliers', permission: 'suppliers.view' },
                { path: '/expenses', permission: 'expenses.view' },
                { path: '/payslips', permissions: ['PAYSLIPS_VIEW', 'PAYSLIPS_MANAGE'] }
            ];
        } else if (userRole === 'operator') {
            accessibleRoutes = [
                { path: '/printer-dashboard', permission: 'orders.view' }
            ];
        } else if (userRole === 'artist') {
            accessibleRoutes = [
                { path: '/layouting-dashboard', permission: 'orders.view' },
                { path: '/printer-dashboard', permission: 'orders.view' },
                { path: '/orders', permission: 'orders.view' },
                { path: '/requisitions', permission: 'requisitions.view' }
            ];
        } else if (['staff', 'staff_qc', 'manager', 'admin'].includes(userRole)) {
            accessibleRoutes = [
                {
                    path: '/sales-dashboard',
                    permissions: ['salesdashboard.view']
                },
                { path: '/orders/board', permission: 'orders.view' },
                { path: '/orders', permission: 'orders.view' },
                { path: '/layouting-dashboard', permission: 'orders.view' },
                { path: '/printer-dashboard', permission: 'orders.view' },
                { path: '/requisitions', permission: 'requisitions.view' },
                { path: '/payments', permission: 'payments.view' },
                { path: '/products', permission: 'products.view' },
                { path: '/clients', permission: 'clients.view' },
                { path: '/suppliers', permission: 'suppliers.view' },
                { path: '/expenses', permission: 'expenses.view' },
                { path: '/payslips', permissions: ['PAYSLIPS_VIEW', 'PAYSLIPS_MANAGE'] },
                {
                    path: '/dashboard',
                    permissions: ['dashboard.view_quick_actions', 'dashboard.view_alerts', 'dashboard.view_stats']
                }
            ];
        } else {
            accessibleRoutes = [
                { path: '/orders/board', permission: 'orders.view' },
                { path: '/orders', permission: 'orders.view' },
                { path: '/requisitions', permission: 'requisitions.view' },
                { path: '/payments', permission: 'payments.view' },
                { path: '/products', permission: 'products.view' },
                { path: '/clients', permission: 'clients.view' },
                { path: '/suppliers', permission: 'suppliers.view' },
                { path: '/expenses', permission: 'expenses.view' },
                { path: '/payslips', permissions: ['PAYSLIPS_VIEW', 'PAYSLIPS_MANAGE'] },
                {
                    path: '/dashboard',
                    permissions: ['dashboard.view_quick_actions', 'dashboard.view_alerts', 'dashboard.view_stats']
                }
            ];
        }

        console.log('Root route: Checking accessible routes for user');

        // Find first route user has permission to access
        for (const route of accessibleRoutes) {
            if (Array.isArray(route.permissions)) {
                if (route.permissions.some(p => authStore.user.permissions.includes(p))) {
                    next(route.path);
                    return;
                }
            } else if (authStore.user.permissions.includes(route.permission)) {
                next(route.path);
                return;
            }
        }

        console.log('Root route: No accessible routes found, redirecting to profile');
        next('/profile');
        return;
    }

    // Skip auth check for login page and public routes
    if (to.path === '/auth/login' || to.meta.requiresAuth === false) {
        next();
        return;
    }

    // OPTIMIZED: Only check initialization if truly needed
    if (authStore.initialized && authStore.isAuthenticated) {
        // Fast path
    } else {
        const hasTokensInStorage = !!localStorage.getItem('token') && !!localStorage.getItem('refreshToken');

        if (!authStore.initialized || (hasTokensInStorage && !authStore.user && !authStore.isAuthenticated)) {
            console.log('Auth store not initialized or needs refresh, initializing...');
            try {
                const initResult = await authStore.initialize();

                if (initResult === false && to.path !== '/auth/login') {
                    console.log('Auth initialization returned false, redirecting to login');
                    next({
                        path: '/auth/login',
                        query: { redirect: to.fullPath }
                    });
                    return;
                }
            } catch (error) {
                console.error('Auth initialization failed:', error);
                if (to.path !== '/auth/login') {
                    next({
                        path: '/auth/login',
                        query: { redirect: to.fullPath }
                    });
                    return;
                }
            }
        }
    }

    // Verify authentication state after initialization
    if (!authStore.isAuthenticated && to.path !== '/auth/login') {
        console.log('Not authenticated after initialization, redirecting to login');
        next({
            path: '/auth/login',
            query: { redirect: to.fullPath }
        });
        return;
    }

    // Handle routes that require authentication
    if (to.matched.some(record => record.meta.requiresAuth)) {
        // Check for required permission (singular)
        if (to.meta.requiredPermission) {
            if (!authStore.hasPermission(to.meta.requiredPermission)) {
                console.log('Permission denied, redirecting to forbidden');
                next({ name: 'forbidden' });
                return;
            }
        }

        // Check for required permissions (plural array)
        if (to.meta.permissions && Array.isArray(to.meta.permissions)) {
            const hasAnyPermission = to.meta.permissions.some(permission =>
                authStore.hasPermission(permission)
            );
            if (!hasAnyPermission) {
                console.log('Permissions denied, redirecting to forbidden');
                next({ name: 'forbidden' });
                return;
            }
        }
    }

    next();
};
